import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { attributeTypes, Images } from 'utils';
import {
    LayoutSection,
    ContentSection,
    BehaviorSection,
    ChartBehaviorSection,
    ConditionsDrawer,
    SessionDataTableSection,
} from './fragments';
import { HeaderFooterProperties } from './headerFooterProperties';
import { FormSettingsProperties } from './formSettingsProperties';
import { SectionSettingsProperties } from './sectionSettingsProperties';
import { useFormBuilder, formPageEnums } from '../formContext';
import { attributesList } from '../attributes';
import { parseGoalBlocks, parseInlineFields } from '../../utils/inlineFields';
import { GoalBlockProperties } from './fragments/goalBlockProperties';
import { InlineFieldProperties } from './fragments/inlineFieldProperties';
import { writeField } from './fragments/writeField';
import { isOwnedGoal, mergeAttribute } from '../../utils/goalAttributes';

/**
 * Write one attribute of a list by index. Goal- and target-level override
 * lists are sparse — an entry exists only once something was changed on that
 * attribute — so the list is padded up to `index` first.
 */
const writeAttributeAt = (attributes = [], index, field, value) => {
    const next = [...attributes];
    while (next.length <= index) next.push({});
    next[index] = writeField(next[index], field, value);
    return next;
};

/**
 * Width and inline are two faces of one layout decision, so setting either
 * settles the other: a Full-width field cannot sit inline, and turning inline
 * on pairs the field with the one above it into two halves. Applies to a
 * section's list and to a goal's own list — target overrides don't carry layout.
 */
const applyLayoutPairing = (attributes, index, field, value) => {
    if (field !== 'width' && field !== 'inline') return attributes;

    const next = [...attributes];
    const patch = (i, properties) => {
        next[i] = { ...next[i], properties: { ...(next[i]?.properties || {}), ...properties } };
    };

    if (field === 'width') {
        if (value === 'Full') patch(index, { inline: false });
        return next;
    }

    if (!value) {
        patch(index, { width: 'Full' });
        return next;
    }

    const row = index > 0 ? [index - 1, index] : [index];
    const width = row.length > 1 ? '1/2' : 'Full';
    row.forEach((i) => patch(i, { width, inline: true }));
    return next;
};

export const Properties = ({ fromReport }) => {
    const {
        formList,
        setFormList,
        selectedIndex,
        activeSection,
        setSelectedIndex,
        selectedGoalId,
        selectedTargetId,
        activeFormPage,
        selectedInlineField,
        setSelectedInlineField,
    } = useFormBuilder();
    const [showConditions, setShowConditions] = useState(false);

    // Reset conditions drawer when switching fields
    useEffect(() => {
        setShowConditions(false);
    }, [selectedIndex]);

    // The field list the panel edits: a goal's own list when a goal of a goal
    // section is picked (it owns its fields), the section's otherwise.
    const listSection = formList?.sections?.[(activeSection || 1) - 1];
    const editsGoalList = !!selectedGoalId && !!listSection?.goalSection && isOwnedGoal(listSection, selectedGoalId);
    const listAttributes = (editsGoalList ? listSection?.goals?.[selectedGoalId]?.attributes : listSection?.attributes) || [];

    // An inline field stays picked only while its rich text is the selected attribute.
    const selectedAttributeId = listAttributes?.[selectedIndex]?.id;
    useEffect(() => {
        if (selectedInlineField && selectedInlineField.attributeId !== selectedAttributeId) setSelectedInlineField(null);
    }, [selectedAttributeId]);

    // Render Header/Footer properties when a document layout page is active
    if (activeFormPage) {
        if (activeFormPage === formPageEnums.FORM_SETTINGS) {
            return <FormSettingsProperties />;
        }
        return <HeaderFooterProperties />;
    }

    const currentSectionIndex = (activeSection || 1) - 1;
    const currentSection = formList?.sections?.[currentSectionIndex];
    const sectionAttributes = listAttributes;
    const templateItem = sectionAttributes?.[selectedIndex];

    // A goal that owns its fields is edited as is; a legacy goal merges its
    // sparse override onto the section field. A picked target merges on top.
    const goalOverride =
        selectedGoalId && !editsGoalList && currentSection?.goals?.[selectedGoalId]?.attributes?.[selectedIndex];
    const targetOverride =
        selectedGoalId &&
        selectedTargetId &&
        currentSection?.goals?.[selectedGoalId]?.targets?.[selectedTargetId]?.attributes?.[selectedIndex];
    const selectedItem = mergeAttribute(mergeAttribute(templateItem, goalOverride), targetOverride);

    /**
     * Replace the edited field list — the picked goal's own list, or the
     * section's. `alignTargets(list)` re-aligns that goal's per-target answer
     * arrays (stored by index) after a field is inserted or removed.
     */
    const updateSectionAttributes = (newAttributes, alignTargets) => {
        setFormList((prev) => {
            const sections = [...(prev?.sections || [])];
            const section = sections[currentSectionIndex];
            if (!section) return prev;

            if (editsGoalList) {
                const goal = { ...(section.goals?.[selectedGoalId] || {}), attributes: newAttributes };
                if (alignTargets && goal.targets) {
                    goal.targets = Object.fromEntries(
                        Object.entries(goal.targets).map(([tid, target]) => [
                            tid,
                            { ...target, attributes: alignTargets([...(target?.attributes || [])]) },
                        ]),
                    );
                }
                sections[currentSectionIndex] = { ...section, goals: { ...(section.goals || {}), [selectedGoalId]: goal } };
            } else {
                sections[currentSectionIndex] = { ...section, attributes: newAttributes };
            }
            return { ...prev, sections };
        });
    };

    const handleSectionUpdate = (field, value) => {
        const newFormList = { ...formList };
        newFormList.sections[currentSectionIndex] = {
            ...currentSection,
            [field]: value,
        };
        setFormList(newFormList);
    };

    // fromReport mode: show ONLY the GROUPING panel, and only for goal sections

    const hasTarget = sectionAttributes.some((a) => a?.type === attributeTypes.TARGET);

    const getFilteredAttributes = (currentIndex) => {
        return attributesList(formList?.model)?.filter((attr) => {
            if (attr.name === 'target' || attr?.name === 'graph') {
                if (!currentSection?.goalSection) return false;
                if (attr.name === 'target' && hasTarget && sectionAttributes[currentIndex]?.type !== attributeTypes.TARGET) {
                    return false;
                }
            }
            return true;
        });
    };

    // Types that use properties.options (Select, Checkbox, Radio)
    const OPTION_BASED_TYPES = [attributeTypes.SELECT, attributeTypes.CHECK_BOX, attributeTypes.RADIO_BUTTON];

    const handleAttributeChange = (index, ev) => {
        const catalog = attributesList(formList?.model);
        const newAttribute = catalog.find((attr) => attr?.type === ev?.target?.value);
        if (!newAttribute) return;

        const currentAttr = sectionAttributes[index];
        const currentDefault = catalog.find((attr) => attr?.type === currentAttr?.type);

        // 1. Preserve label if the user has customized it (differs from the default)
        const userChangedLabel = currentAttr?.label && currentAttr.label !== currentDefault?.label;

        // 2. Determine if we should preserve options (switching between option-based types)
        const fromOptionBased = OPTION_BASED_TYPES.includes(currentAttr?.type);
        const toOptionBased = OPTION_BASED_TYPES.includes(newAttribute?.type);
        const preserveOptions = fromOptionBased && toOptionBased;

        const newAttributes = [...sectionAttributes];
        newAttributes[index] = {
            ...newAttribute,
            id: currentAttr.id,
            // Keep the existing label if user customized it
            ...(userChangedLabel ? { label: currentAttr.label } : {}),
            properties: {
                ...newAttribute.properties,
                // Preserve existing options when switching between option-based types
                ...(preserveOptions && currentAttr?.properties?.options
                    ? { options: currentAttr.properties.options }
                    : {}),
            },
        };
        updateSectionAttributes(newAttributes);
    };

    const handleDuplicate = (index) => {
        const original = sectionAttributes[index];
        const baseName = original.name.replace(/\s\d+$/, '');
        const sameNames = sectionAttributes.filter((a) => a?.name?.startsWith(baseName));
        const duplicateCount = sameNames.length;
        const duplicated = {
            ...original,
            id: uuidv4(),
            name: `${baseName} ${duplicateCount}`,
            properties: { ...original.properties },
        };

        const newAttributes = [...sectionAttributes];
        newAttributes.splice(index + 1, 0, duplicated);
        updateSectionAttributes(newAttributes, (list) => {
            list.splice(index + 1, 0, list[index] ? { ...list[index] } : {});
            return list;
        });
    };

    const handleDelete = (index) => {
        const newAttributes = [...sectionAttributes];
        newAttributes.splice(index, 1);
        updateSectionAttributes(newAttributes, (list) => {
            list.splice(index, 1);
            return list;
        });
        if (selectedIndex === index) setSelectedIndex(null);
    };

    /**
     * Write one field of the selected attribute — on the goal/target override
     * when a goal element is selected, on the section template otherwise.
     *
     * The update is functional on purpose: a single event handler often writes
     * several fields in a row (the signature auto-fill toggle sets
     * `signatureFill`, `readOnly` and `signerUser`), and deriving each write
     * from the previous state — rather than from the `formList` captured by
     * this render — is what lets those writes compose instead of overwriting
     * one another.
     */
    const handleUpdate = (field, value) => {
        if (selectedIndex === null || selectedIndex === undefined) return;

        setFormList((prev) => {
            const sections = [...(prev?.sections || [])];
            const section = sections[currentSectionIndex];
            if (!section) return prev;

            if (selectedGoalId && section.goalSection) {
                const goals = { ...(section.goals || {}) };
                const goal = { ...(goals[selectedGoalId] || {}) };

                if (selectedTargetId) {
                    const targets = { ...(goal.targets || {}) };
                    const target = { ...(targets[selectedTargetId] || {}) };
                    target.attributes = writeAttributeAt(target.attributes, selectedIndex, field, value);
                    targets[selectedTargetId] = target;
                    goal.targets = targets;
                } else {
                    goal.attributes = writeAttributeAt(goal.attributes, selectedIndex, field, value);
                    // A goal that owns its fields carries its own layout too.
                    if (goal.ownAttributes) {
                        goal.attributes = applyLayoutPairing(goal.attributes, selectedIndex, field, value);
                    }
                }

                goals[selectedGoalId] = goal;
                sections[currentSectionIndex] = { ...section, goals };
                return { ...prev, sections };
            }

            const attributes = applyLayoutPairing(
                writeAttributeAt(section.attributes, selectedIndex, field, value),
                selectedIndex,
                field,
                value,
            );

            sections[currentSectionIndex] = { ...section, attributes };
            return { ...prev, sections };
        });
    };

    const showGrouping = fromReport && currentSection?.goalSection;

    const renderGroupingPanel = () => (
        <div className="grouping-properties-panel" style={{ border: 'none', width: '100%' }}>
            <div className="grouping-properties-header">
                <span>GROUPING</span>
            </div>
            <div className="behavior-row">
                <div className="behavior-info">
                    <span className="behavior-title">Group by Domain</span>
                    <span className="behavior-desc" style={{ fontSize: 10 }}>
                        Organize goals under their domain categories
                    </span>
                </div>
                <label className="toggle-switch">
                    <input
                        type="checkbox"
                        checked={!!currentSection?.groupByDomain}
                        onChange={(e) => handleSectionUpdate('groupByDomain', e.target.checked)}
                    />
                    <span className="slider" />
                </label>
            </div>

            {currentSection?.groupByDomain && (
                <>
                    <div className="properties-divider" />
                    <div className="behavior-row" style={{ margin: '16px 0 0' }}>
                        <div className="behavior-info">
                            <span className="behavior-title">Show Domain Labels</span>
                            <span className="behavior-desc" style={{ fontSize: 10 }}>
                                Display domain name headers above each group
                            </span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={!!currentSection?.showDomainLabels}
                                onChange={(e) => handleSectionUpdate('showDomainLabels', e.target.checked)}
                            />
                            <span className="slider" />
                        </label>
                    </div>
                    <div className="properties-info-box">
                        <img src={Images.grouppedFrame} alt="grouped" />
                        <span className="properties-info-text">
                            Goals are organized by domain. Toggle label visibility to show or hide domain headers.
                        </span>
                    </div>
                </>
            )}
        </div>
    );

    if (!selectedItem) {
        if (showGrouping) {
            return (
                <div className="properties-wrapper">
                    <div className="properties-header">
                        <h3>Properties</h3>
                    </div>
                    {renderGroupingPanel()}
                    <div className="properties-divider" />
                    {/* Goal sections keep their name, hide-title and title style here too. */}
                    <SectionSettingsProperties embedded />
                </div>
            );
        }
        // A section is selected but none of its fields — show the section's own settings.
        if (activeSection && currentSection) {
            return <SectionSettingsProperties />;
        }
        return (
            <div className="properties-wrapper empty">
                <p>Select an element to edit properties</p>
            </div>
        );
    }

    const inlineFieldEntry =
        selectedItem?.type === attributeTypes.RICH_TEXT && selectedInlineField?.attributeId === selectedItem?.id
            ? parseInlineFields(selectedItem?.value).find((entry) => entry.id === selectedInlineField.fieldId)
            : null;
    const goalBlockEntry =
        selectedItem?.type === attributeTypes.RICH_TEXT && selectedInlineField?.attributeId === selectedItem?.id
            ? parseGoalBlocks(selectedItem?.value).find((entry) => entry.id === selectedInlineField.fieldId)
            : null;
    if (goalBlockEntry) {
        return (
            <GoalBlockProperties
                richText={selectedItem}
                entry={goalBlockEntry}
                handleUpdate={handleUpdate}                onBack={() => setSelectedInlineField(null)}
                onClose={() => {
                    setSelectedInlineField(null);
                    setSelectedIndex(null);
                }}
            />
        );
    }

    if (inlineFieldEntry) {
        return (
            <InlineFieldProperties
                richText={selectedItem}
                entry={inlineFieldEntry}
                handleUpdate={handleUpdate}
                onBack={() => setSelectedInlineField(null)}
                onClose={() => {
                    setSelectedInlineField(null);
                    setSelectedIndex(null);
                }}
            />
        );
    }

    // When the conditions drawer is open, render it instead of the normal panel
    if (showConditions && selectedItem) {
        return (
            <div className="properties-wrapper">
                <ConditionsDrawer
                    selectedItem={selectedItem}
                    handleUpdate={handleUpdate}
                    allAttributes={sectionAttributes}
                    onBack={() => setShowConditions(false)}
                />
            </div>
        );
    }

    return (
        <div className="properties-wrapper">
            <div className="properties-header">
                <h3>Properties</h3>
                <button className="reset-btn-by-default" onClick={() => setSelectedIndex(null)}>
                    <img src={Images.closeSingleVector} alt="close" style={{ width: 18, height: 18 }} />
                </button>
            </div>

            {showGrouping && renderGroupingPanel()}
            <div className="properties-divider" />

            {selectedItem?.type !== attributeTypes.RICH_TEXT &&
                selectedItem?.type !== attributeTypes.TABLE &&
                selectedItem?.type !== attributeTypes.SESSION_DATA_TABLE &&
                selectedItem?.type !== attributeTypes.GRAPH &&
                selectedItem?.type !== attributeTypes.TARGET && (
                    <>
                        <LayoutSection selectedItem={selectedItem} handleUpdate={handleUpdate} index={selectedIndex} />
                        <div className="properties-divider" />
                    </>
                )}

            <ContentSection
                selectedItem={selectedItem}
                handleUpdate={handleUpdate}
                handleAttributeChange={handleAttributeChange}
                getFilteredAttributes={getFilteredAttributes}
                selectedIndex={selectedIndex}
            />
            <div className="properties-divider"></div>

            {selectedItem?.type === attributeTypes.SESSION_DATA_TABLE && (
                <SessionDataTableSection selectedItem={selectedItem} handleUpdate={handleUpdate} />
            )}

            {selectedItem?.type !== attributeTypes.GRAPH &&
                selectedItem?.type !== attributeTypes.TARGET &&
                selectedItem?.type !== attributeTypes.SESSION_DATA_TABLE &&
                selectedItem?.type !== attributeTypes.TABLE && (
                    <BehaviorSection
                        selectedItem={selectedItem}
                        handleUpdate={handleUpdate}
                        onOpenConditions={() => setShowConditions(true)}
                    />
                )}

            {selectedItem?.type === attributeTypes.TARGET && (
                <BehaviorSection
                    selectedItem={selectedItem}
                    handleUpdate={handleUpdate}
                    onOpenConditions={() => setShowConditions(true)}
                />
            )}

            {selectedItem?.type === attributeTypes.GRAPH && (
                <ChartBehaviorSection selectedItem={selectedItem} handleUpdate={handleUpdate} />
            )}

            {selectedItem?.type === attributeTypes.TARGET && selectedItem?.properties?.showGraph && (
                <ChartBehaviorSection selectedItem={selectedItem} handleUpdate={handleUpdate} />
            )}

            {!selectedItem?.properties?.updateForbidden && (
                <>
                    <div className="properties-divider" />
                    <div className="properties-section">
                        <div className="section-header">
                            <img src={Images.behaivorIcon} alt="actions" />
                            <span>ACTIONS</span>
                        </div>
                        <div className="properties-actions-row">
                            <button className="properties-action-btn" onClick={() => handleDuplicate(selectedIndex)}>
                                <img src={Images.duplicateIcon} alt="duplicate" />
                                <span>Duplicate</span>
                            </button>
                            <button className="properties-action-btn delete" onClick={() => handleDelete(selectedIndex)}>
                                <img src={Images.trash} alt="delete" />
                                <span>Delete</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
